import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BiRedo, BiUndo } from 'react-icons/bi';

import PageContainer from '../../components/page-container';
import Message from '../../components/toast-message';
import FormLoader from '../../components/form-loader/form-loader';
import PlaybookName from './components/playbook-name';
import Button from '../../components/button/button';
import GenerateDialog from './components/generate-dialog';
import VariableDialog from './components/variable-dialog';
import { useAppInfoFromUralParams } from '../../helpers/hooks/common-hook';

import { listCodehook } from '../../services/model/model-config-service';
import { listDataSources } from '../../services/knowledge';
import { journeyList } from '../../services/model/journey-service';

import { PlaybookContext, PlaybookContextProvider, LOAD_STATE } from './context/PlaybookContext';
import StepsPanel, { PANEL } from './components/steps-panel';

const historyButtonClass = 'inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 '
    + 'hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-40 '
    + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

/**
 * The playbook editor page.
 *
 * A single column: the numbered step list, with each step's settings opening
 * inline beneath its own row. The icon rail switches to the app's variables.
 */

function Editor() {
    const { appId } = useAppInfoFromUralParams();
    const { playbookId } = useParams();
    const navigate = useNavigate();

    const {
        metadata, setMetadata, loadState, load,
        aiGenStatus, aiBusy, aiPollError, beginGeneration,
        syncing, saveError, saveSuccess, compileErrors,
        saveNow, hasUnsavedChanges, autoSaveEnabled, setAutoSave,
        canUndo, canRedo, historyBusy, undo, redo,
    } = useContext(PlaybookContext);

    const [generating, setGenerating] = useState(false);
    const [panel, setPanel] = useState(PANEL.STEPS);
    const [codehookList, setCodehookList] = useState([]);
    const [knowledgeList, setKnowledgeList] = useState([]);
    const [journeys, setJourneys] = useState([]);
    const [slotsDialogOpen, setSlotsDialogOpen] = useState(false);
    // 'create' opens the new-variable form; 'browse' opens the list.
    const [slotsDialogMode, setSlotsDialogMode] = useState('browse');

    useEffect(() => {
        if (appId && playbookId) load(appId, playbookId);
    }, [appId, playbookId, load]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const { data } = await listCodehook(appId);
                // The list identifies a function by `codehookId`, which is
                // also what the runtime looks it up by.
                setCodehookList((data || []).map(c => ({
                    id: c.codehookId || c.id, name: c.codehookId || c.name,
                })));
            } catch (e) { /* the picker just stays empty */ }
            try {
                const list = await journeyList(appId);
                setJourneys((list?.data || list || []).filter(j => j.id !== playbookId));
            } catch (e) { /* likewise */ }
            try {
                const { response } = await listDataSources(appId);
                // `file` is the indexed file name a "read the file" search
                // needs - the same name the canvas doc-search builds.
                setKnowledgeList((response?.data || []).map(ds => ({
                    id: ds.dataSourceId, label: ds.dataSourceId,
                    file: `${appId}-${ds.id}.${ds.fileType}`,
                })));
            } catch (e) { /* likewise */ }
        })();
    }, [appId, playbookId]);

    // Opening the variables panel, or a "create variable" request from a step
    // editor, both open the compact playbook variable dialog.
    useEffect(() => {
        if (panel !== PANEL.VARIABLES) return;
        setSlotsDialogMode('browse');
        setSlotsDialogOpen(true);
    }, [panel]);

    // The variables the pickers offer. metadata.slots is the journey's slot map,
    // refreshed whenever the slot dialog saves.
    const slotOptions = useMemo(
        () => Object.keys(metadata.slots || {}).map(name => ({ value: name, label: name })),
        [metadata.slots],
    );

    const canSave = hasUnsavedChanges && !syncing && !aiBusy && compileErrors.length === 0;
    // With auto save on, edits are saved within a moment, so they are not flagged as unsaved.
    const showUnsaved = hasUnsavedChanges && !autoSaveEnabled;
    const historyLocked = historyBusy || syncing || aiBusy;

    // Undo/redo restore a saved version, which replaces edits not saved yet.
    const confirmDiscard = () => !hasUnsavedChanges
        || window.confirm('You have unsaved changes. Discard them and restore the saved version?');

    // Cmd/Ctrl+S saves, and the browser warns before unsaved edits are lost.
    useEffect(() => {
        const onKeyDown = e => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (canSave) saveNow();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [canSave, saveNow]);

    useEffect(() => {
        if (!hasUnsavedChanges) return undefined;
        const onBeforeUnload = e => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [hasUnsavedChanges]);

    const lookups = useMemo(() => ({
        codehookList,
        knowledgeList,
        journeyList: journeys,
        codehooks: Object.fromEntries(codehookList.map(c => [c.id, c.name || c.id])),
        journeys: Object.fromEntries(journeys.map(j => [j.id, j.name || j.id])),
    }), [codehookList, knowledgeList, journeys]);

    if (loadState === LOAD_STATE.LOADING) {
        return <PageContainer title="Playbook"><FormLoader relative text="Loading" /></PageContainer>;
    }

    if (loadState === LOAD_STATE.CANVAS_OWNED) {
        return (
            <PageContainer title="Playbook">
                <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 px-6 py-8 text-center">
                    <h3 className="text-lg font-semibold text-amber-900">This workflow was built on the canvas</h3>
                    <p className="mx-auto mt-2 max-w-md text-sm text-amber-800">
                        Opening it here would replace its layout with a compiled step list. Edit it in the
                        workflow editor instead, or create a new playbook.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate(`/app/${appId}/journey/${playbookId}`)}
                        className="mt-5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-fixed-white hover:bg-amber-700"
                    >
                        Open in the workflow editor
                    </button>
                </div>
            </PageContainer>
        );
    }

    if (loadState === LOAD_STATE.FAILED) {
        return (
            <PageContainer title="Playbook">
                <div className="mt-10 text-center text-sm text-gray-500">
                    This playbook could not be loaded.
                </div>
            </PageContainer>
        );
    }

    const status = syncing
        ? 'Saving…'
        : compileErrors.length > 0
            ? `${compileErrors.length} thing${compileErrors.length === 1 ? '' : 's'} to fix before this can be saved`
            : hasUnsavedChanges ? (autoSaveEnabled ? 'Saving…' : 'Unsaved changes')
                : saveSuccess ? 'All changes saved' : '';

    return (
        <PageContainer
            title={<PlaybookName />}
            headerAction={
                <div className="flex items-center gap-2">
                    {/* Undo / redo step through saved versions, which only manual saves create. */}
                    {!autoSaveEnabled && (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                title="Undo"
                                aria-label="Undo"
                                onClick={() => confirmDiscard() && undo()}
                                disabled={!canUndo || historyLocked}
                                className={historyButtonClass}
                            >
                                <BiUndo className="h-[18px] w-[18px]" />
                            </button>
                            <button
                                type="button"
                                title="Redo"
                                aria-label="Redo"
                                onClick={() => confirmDiscard() && redo()}
                                disabled={!canRedo || historyLocked}
                                className={historyButtonClass}
                            >
                                <BiRedo className="h-[18px] w-[18px]" />
                            </button>
                        </div>
                    )}

                    <label className="flex h-8 cursor-pointer items-center gap-2 px-1">
                        <span className="relative inline-flex items-center">
                            <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={autoSaveEnabled}
                                onChange={e => setAutoSave(e.target.checked)}
                            />
                            <span
                                className="relative h-5 w-9 shrink-0 rounded-full bg-gray-200 transition-colors duration-200
                                    after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-fixed-white
                                    after:shadow-sm after:transition-transform after:duration-200 after:content-['']
                                    peer-checked:bg-brand-lime peer-checked:after:translate-x-4
                                    peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1"
                            />
                        </span>
                        <span className="whitespace-nowrap text-xs font-medium leading-none text-gray-600">Auto save</span>
                    </label>

                    {/* <Button text="Create with AI" color="default" disabled={aiBusy || syncing} onClick={() => setGenerating(true)} /> */}
                    {!autoSaveEnabled && (
                        <Button
                            text={syncing ? 'Saving' : 'Save'}
                            loading={syncing}
                            // A pulsing dot on the button until the edits are saved, so they are not forgotten.
                            isIndicated={showUnsaved && !syncing}
                            aria-label={showUnsaved ? 'Save unsaved changes' : 'Save'}
                            disabled={!canSave}
                            title={compileErrors.length > 0 ? 'Fix the highlighted steps to save' : 'Save (Ctrl+S)'}
                            onClick={saveNow}
                        />
                    )}
                </div>
            }
            subtitle={status}
            titleMarker={compileErrors.length > 0 ? 'Needs attention' : showUnsaved ? 'Unsaved changes' : undefined}
            titleMarkerColor={compileErrors.length > 0 || showUnsaved ? 'warning' : 'success'}
        >
            {saveError && <Message text={saveError} type="failure" />}

            {aiGenStatus && (
                <div role="status" className={`mb-4 rounded-xl border px-4 py-3 text-sm ${aiGenStatus.state === 'failed' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                    {aiBusy ? `AI is ${aiGenStatus.state === 'queued' ? 'getting started' : aiGenStatus.state} your playbook${aiGenStatus.attempt ? ` · Attempt ${aiGenStatus.attempt} of ${aiGenStatus.maxAttempts || 5}` : ''}…`
                        : aiGenStatus.state === 'complete' ? 'Your AI-generated playbook is saved and ready to edit.' : aiGenStatus.error || 'Generation failed. Please try again.'}
                    {aiPollError && <p className="mt-1 text-xs">{aiPollError}</p>}
                </div>
            )}
            {generating && <GenerateDialog existing onClose={() => setGenerating(false)} onGenerate={beginGeneration} />}

            {/* One column: each step's settings open inline beneath its own row,
                so there is no second pane to keep in sync with the selection. */}
            <fieldset disabled={aiBusy} aria-busy={aiBusy} className={`mt-4 h-[calc(100vh-190px)] min-w-0 ${aiBusy ? 'pointer-events-none opacity-60' : ''}`}>
                <StepsPanel
                    panel={panel}
                    onPanelChange={setPanel}
                    onClose={() => {
                        if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Leave without saving?')) return;
                        navigate(`/app/${appId}/playbooks`);
                    }}
                    lookups={lookups}
                    slotOptions={slotOptions}
                    onCreateVariable={() => {
                        setSlotsDialogMode('create');
                        setSlotsDialogOpen(true);
                    }}
                />
            </fieldset>

            {slotsDialogOpen && (
                <VariableDialog
                    appId={appId}
                    journeyId={playbookId}
                    slots={metadata.slots}
                    startOnCreate={slotsDialogMode === 'create'}
                    onSaved={slots => setMetadata(prev => ({ ...prev, slots }))}
                    onClose={() => {
                        setSlotsDialogOpen(false);
                        if (panel === PANEL.VARIABLES) setPanel(PANEL.STEPS);
                    }}
                />
            )}
        </PageContainer>
    );
}

export default function PlaybookEditorPage() {
    return (
        <PlaybookContextProvider>
            <Editor />
        </PlaybookContextProvider>
    );
}
